import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { string } from 'yup';

import {
  changeProfilePassword,
  getProfileData,
  updateProfileData,
  getCompanyUser,
  updateAgentProfile,
} from '../actions';
import { profileDataSelector, userSelector } from '../selectors';
import { formatPhoneNumber } from 'helpers/formatPhoneNumber';
import { validatePhoneNumberRegex } from 'regex/phoneRegex';
import {
  CIRCULAR_PROGRESS_SIZE_NAMES,
  CIRCULAR_PROGRESS_TYPES,
} from 'components/circularProgress/constants';
import { BUTTON_TYPES } from 'components/button/constants';
import { TOAST_TYPES } from 'constants';
import { TOAST_MESSAGES } from 'constants';
import { Toastify } from 'hooks';

import { CircularProgress, Input, NavigationControl, Select } from 'components';
import { AgentNoImage } from 'components/agentNoImage';
import { Button } from 'components/button';
import { PROFILE_SETTINGS } from 'constants';

const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL;

export function ProfileSettings() {
  const { pathname } = useLocation();
  const { id } = useParams();

  const isProfileEdit = pathname.includes('profile');
  const profileData = useSelector(
    isProfileEdit ? profileDataSelector : userSelector
  );
  const user = useSelector((state) => state.auth.user);
  const isSavingProgress = useSelector((state) => state.users.isSavingProgress);
  const isUploadingProfileImage = useSelector(
    (state) => state.users.isUploadingProfileImage
  );

  const [isPasswordChange, setIsPasswordChange] = useState(false);
  const [isNotMatch, setIsNotMatch] = useState(false);
  const [isNotValidPassword, setIsNotValidPassword] = useState(false);
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isValidPhone, setIsValidPhone] = useState(true);
  const [profile, setProfile] = useState({
    name: '',
    last_name: '',
    phone: '',
    email: '',
    description: '',
    position: '',
    image: '',
  });
  const [changePassword, setChangePassword] = useState({
    old_password: '',
    password: '',
    password_confirmation: '',
  });
  const [isEditing, setIsEditing] = useState({
    email: false,
    borkerage: false,
    phone: false,
    description: false,
    name: false,
    position: false,
  });

  const dispatch = useDispatch();

  const inputRef = useRef(null);

  const setFormData = (data) => {
    const formData = new FormData();
    Object.keys(data).forEach((key) => {
      formData.append(key, data[key] || '');
    });
    return formData;
  };

  const changeIsEditing = (field, value) => {
    let editing = {};
    setIsValidEmail(true);
    setIsValidPhone(true);
    Object.keys(isEditing).map((el) => (editing[el] = false));
    setIsEditing({
      ...editing,
      [field]: value,
    });
  };

  const handleChangePassword = (e) => {
    setChangePassword((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const savePassword = (e) => {
    e.preventDefault();
    setIsNotMatch(false);
    setIsNotValidPassword(false);
    if (
      changePassword.password === changePassword.password_confirmation &&
      changePassword.password.length > 7 &&
      changePassword.old_password
    ) {
      dispatch(
        changeProfilePassword(changePassword, () => {
          setChangePassword({
            old_password: '',
            password: '',
            password_confirmation: '',
          });
          setIsPasswordChange(false);
        })
      )
        .then(() => {
          Toastify({
            type: TOAST_TYPES.success,
            message: TOAST_MESSAGES.passwordChanged,
          });
        })
        .catch(() => {
          Toastify({
            type: TOAST_TYPES.error,
            message: TOAST_MESSAGES.wrongPassword,
          });
        });
    } else if (
      changePassword.password !== changePassword.password_confirmation
    ) {
      setIsNotMatch(true);
    } else if (changePassword.password.length < 8) {
      setIsNotValidPassword(true);
    }
  };

  const handleChangeProfile = (e, name) => {
    if (name === 'phone') {
      const formattedPhoneNumber = formatPhoneNumber(e.target.value);
      setProfile((prev) => ({
        ...prev,
        [name]: formattedPhoneNumber,
      }));
    } else {
      setProfile((prev) => ({
        ...prev,
        [name]: e.target.value,
      }));
    }
  };

  const saveProfileChange = (name) => {
    let data = {};
    if (name === 'name') {
      data = {
        name: profile.name,
        last_name: profile.last_name,
      };
    } else if (name === 'email') {
      if (string().email().isValidSync(profile.email)) {
        data = {
          email: profile.email,
        };
      } else {
        setIsValidEmail(false);
        setIsValidEmail(string().email().isValidSync(profile.email));
      }
    } else if (name === 'phone') {
      if (validatePhoneNumberRegex.test(profile.phone)) {
        data = {
          phone: profile.phone,
        };
      } else {
        setIsValidPhone(false);
        setIsValidPhone(validatePhoneNumberRegex.test(profile.phone));
      }
    } else {
      data = {
        [name]: profile[name],
      };
    }
    if (Object.keys(data).length) {
      if (isProfileEdit) {
        dispatch(updateProfileData({ ...data, image: profile.image }))
          .then(() => {
            Toastify({
              type: TOAST_TYPES.success,
              message: TOAST_MESSAGES.savedChanges,
            });
          })
          .catch(() => {
            Toastify({
              type: TOAST_TYPES.error,
              message: TOAST_MESSAGES.networkCheck,
            });
          })
          .finally(() => changeIsEditing(name, false));
      } else {
        dispatch(
          updateAgentProfile(
            id,
            setFormData({ ...profile, ...data, image: profile.image })
          )
        )
          .then(() => {
            Toastify({
              type: TOAST_TYPES.success,
              message: TOAST_MESSAGES.savedChanges,
            });
          })
          .catch(() => {
            Toastify({
              type: TOAST_TYPES.error,
              message: TOAST_MESSAGES.networkCheck,
            });
          })
          .finally(() => changeIsEditing(name, false));
      }
    }
  };

  const cancelProfileChange = (name) => {
    if (name === 'name') {
      setProfile((prev) => ({
        ...prev,
        name: profileData.name,
        last_name: profileData.last_name,
      }));
    } else {
      setProfile((prev) => ({
        ...prev,
        [name]: profileData[name],
      }));
    }
  };

  const openFileUploadWindow = () => {
    inputRef.current.click();
  };

  const changeProfilePhoto = (e) => {
    const fileObj = e.target.files && e.target.files[0];
    if (fileObj) {
      const isChangingImage = true;
      const formData = new FormData();
      formData.append('image', fileObj);
      if (isProfileEdit) {
        dispatch(updateProfileData(formData))
          .then(() => {
            Toastify({
              type: TOAST_TYPES.success,
              message: TOAST_MESSAGES.imageChanged,
            });
          })
          .catch(() => {
            Toastify({
              type: TOAST_TYPES.error,
              message: TOAST_MESSAGES.imageUpdateFailed,
            });
          });
      } else {
        Object.keys(profile).forEach((key) => {
          if (key !== 'image') {
            formData.append(key, profile[key] || '');
          }
        });
        dispatch(updateAgentProfile(id, formData, isChangingImage))
          .then(() => {
            Toastify({
              type: TOAST_TYPES.success,
              message: TOAST_MESSAGES.imageChanged,
            });
          })
          .catch(() => {
            Toastify({
              type: TOAST_TYPES.error,
              message: TOAST_MESSAGES.imageUpdateFailed,
            });
          });
      }
    }
  };

  useEffect(() => {
    isProfileEdit ? dispatch(getProfileData()) : dispatch(getCompanyUser(id));
  }, []);

  useEffect(() => {
    setProfile(profileData);
  }, [profileData]);

  return (
    <div className="content">
      <NavigationControl isBack title="Profile Settings" />
      <div className="dashboardContent">
        <div className="card">
          <h3 className="heading3">{PROFILE_SETTINGS.title}</h3>
          <div className="row">
            <div className="col-xl-3 col-lg-4">
              <div className="profileInfoCard">
                <div className="titleImg">
                  <div className="avatar">
                    {!isUploadingProfileImage ? (
                      <>
                        {profile.image ? (
                          <img
                            src={imageBaseUrl + profile.image}
                            alt="avatar"
                          />
                        ) : (
                          <AgentNoImage
                            name={profile.name}
                            surname={profile.last_name}
                          />
                        )}
                      </>
                    ) : (
                      <CircularProgress
                        type={CIRCULAR_PROGRESS_TYPES.white}
                        size={CIRCULAR_PROGRESS_SIZE_NAMES.small}
                      />
                    )}
                  </div>
                  <input
                    style={{ display: 'none' }}
                    ref={inputRef}
                    type="file"
                    onChange={changeProfilePhoto}
                  />
                  <button onClick={openFileUploadWindow}>{PROFILE_SETTINGS.update_photo}</button>
                </div>
                {isEditing.name ? (
                  <div className="info">
                    <div className="media-body">
                      <Input
                        isLabeShow={false}
                        autoComplete="off"
                        id="name"
                        value={profile.name}
                        placeholder={PROFILE_SETTINGS.enter_name}
                        name="name"
                        onChange={(e) => handleChangeProfile(e, 'name')}
                      />
                      <Input
                        isLabeShow={false}
                        autoComplete="off"
                        id="last_name"
                        value={profile.last_name}
                        placeholder={PROFILE_SETTINGS.enter_surname}
                        name="last_name"
                        onChange={(e) => handleChangeProfile(e, 'last_name')}
                      />
                      <Button
                        type={BUTTON_TYPES.main_save}
                        loading={isSavingProgress}
                        customClasses="btn cta-primary"
                        onClick={() => {
                          saveProfileChange('name');
                        }}
                        text={PROFILE_SETTINGS.save}
                      />
                    </div>
                    <u
                      className="ml-2"
                      onClick={() => {
                        changeIsEditing('name', false);
                        cancelProfileChange('name');
                      }}
                    >
                      {PROFILE_SETTINGS.cancel}
                    </u>
                  </div>
                ) : (
                  <div className="info">
                    <div className="media-body">
                      <h4>{PROFILE_SETTINGS.name_surname}</h4>
                      <p>{`${profile?.name ?? ''} ${
                        profile?.last_name ?? ''
                      }`}</p>
                    </div>
                    <u onClick={() => changeIsEditing('name', true)}>{PROFILE_SETTINGS.edit}</u>
                  </div>
                )}
                {isEditing.position ? (
                  <div className="info">
                    <div className="media-body">
                      <Select
                        id="position"
                        label="Designation"
                        name="position"
                        options={[
                          {
                            label: 'Sales Representative',
                            value: 'Sales Representative',
                          },
                          {
                            label: 'Broker',
                            value: 'Broker',
                          },
                        ]}
                        placeholder="Select designation"
                        value={profile.position}
                        onChange={(e) => handleChangeProfile(e, 'position')}
                      />
                      <Button
                        type={BUTTON_TYPES.main_save}
                        loading={isSavingProgress}
                        customClasses="btn cta-primary"
                        onClick={() => {
                          saveProfileChange('position');
                        }}
                        text={PROFILE_SETTINGS.save}
                      />
                    </div>
                    <u
                      className="ml-2 profile_edit_cancel"
                      onClick={() => {
                        changeIsEditing('position', false);
                        cancelProfileChange('position');
                      }}
                    >
                      {PROFILE_SETTINGS.cancel}
                    </u>
                  </div>
                ) : (
                  <div className="info">
                    <div className="media-body">
                      <h4>{PROFILE_SETTINGS.designation}</h4>
                      <p>{profile?.position ||PROFILE_SETTINGS.not_provided}</p>
                    </div>
                    <u
                      onClick={() => {
                        setProfile((prev) => ({
                          ...prev,
                          position: profile?.position,
                        }));
                        changeIsEditing('position', true);
                      }}
                    >
                      {profile?.position?.length ? PROFILE_SETTINGS.edit : PROFILE_SETTINGS.add}
                    </u>
                  </div>
                )}
              </div>
            </div>
            <div className="col-xl-9 col-lg-8">
              <h4 className="heading4">{PROFILE_SETTINGS.personal_information}</h4>
              {isEditing.email ? (
                <div className="editInfo">
                  <div className="media-body">
                    <h4>{PROFILE_SETTINGS.email}</h4>
                    <Input
                      isLabeShow={false}
                      autoComplete="off"
                      id="email"
                      value={profile.email}
                      placeholder={PROFILE_SETTINGS.email}
                      name="email"
                      error={!isValidEmail}
                      onChange={(e) => handleChangeProfile(e, 'email')}
                      helperText={PROFILE_SETTINGS.email_error}
                    />
                    <Button
                      type={BUTTON_TYPES.main_save}
                      loading={isSavingProgress}
                      customClasses="btn cta-primary"
                      onClick={() => {
                        saveProfileChange('email');
                      }}
                      text="Save"
                    />
                  </div>
                  <u
                    onClick={() => {
                      changeIsEditing('email', false);
                      cancelProfileChange('email');
                    }}
                  >
                    {PROFILE_SETTINGS.cancel}
                  </u>
                </div>
              ) : (
                <div className="editInfo">
                  <div className="media-body">
                    <h4>{PROFILE_SETTINGS.email}</h4>
                    <p>{profile?.email}</p>
                  </div>
                  <u onClick={() => changeIsEditing('email', true)}>{PROFILE_SETTINGS.edit}</u>
                </div>
              )}
              <div className="editInfo">
                <div className="media-body">
                  <h4>{PROFILE_SETTINGS.brokerage_title}</h4>
                  <a
                    rel="noreferrer"
                    href={profileData?.site_url || PROFILE_SETTINGS.brokerage_url}
                    target="_blank"
                  >
                    {PROFILE_SETTINGS.brokerage}
                  </a>
                </div>
              </div>
              {isEditing.phone ? (
                <div className="editInfo">
                  <div className="media-body">
                    <h4>{PROFILE_SETTINGS.phone_number}</h4>
                    <Input
                      isLabeShow={false}
                      autoComplete="off"
                      id="phone"
                      value={profile.phone}
                      placeholder={PROFILE_SETTINGS.enter_phone}
                      name="phone"
                      error={!isValidPhone}
                      onChange={(e) => handleChangeProfile(e, 'phone')}
                      helperText={PROFILE_SETTINGS.phone_error}
                    />
                    <Button
                      type={BUTTON_TYPES.main_save}
                      loading={isSavingProgress}
                      customClasses="btn cta-primary"
                      onClick={() => {
                        saveProfileChange('phone');
                      }}
                      text={PROFILE_SETTINGS.save}
                    />
                  </div>
                  <u
                    onClick={() => {
                      changeIsEditing('phone', false);
                      cancelProfileChange('phone');
                    }}
                  >
                    {PROFILE_SETTINGS.cancel}
                  </u>
                </div>
              ) : (
                <div className="editInfo">
                  <div className="media-body">
                    <h4>{PROFILE_SETTINGS.phone_number}</h4>
                    <p>{profile?.phone ?? PROFILE_SETTINGS.not_provided}</p>
                  </div>
                  <u onClick={() => changeIsEditing('phone', true)}>
                    {profile?.phone?.length ? PROFILE_SETTINGS.edit : PROFILE_SETTINGS.add}
                  </u>
                </div>
              )}
              {isEditing.description ? (
                <div className="editInfo">
                  <div className="media-body">
                    <h4>{PROFILE_SETTINGS.short_description}</h4>
                    <div className="form-group">
                      <textarea
                        name="description"
                        id="description"
                        cols="30"
                        rows="10"
                        value={profile.description}
                        className="form-control"
                        placeholder={PROFILE_SETTINGS.enter_desc}
                        onChange={(e) => handleChangeProfile(e, 'description')}
                      />
                    </div>
                    <Button
                      type={BUTTON_TYPES.main_save}
                      loading={isSavingProgress}
                      customClasses="btn cta-primary"
                      onClick={() => {
                        saveProfileChange('description');
                      }}
                      text={PROFILE_SETTINGS.save}
                    />
                  </div>
                  <u
                    onClick={() => {
                      changeIsEditing('description', false);
                      cancelProfileChange('description');
                    }}
                  >
                    {PROFILE_SETTINGS.cancel}
                  </u>
                </div>
              ) : (
                <div className="editInfo">
                  <div className="media-body">
                    <h4>{PROFILE_SETTINGS.short_description}</h4>
                    <p>{profile?.description ?? PROFILE_SETTINGS.not_provided}</p>
                  </div>
                  <u onClick={() => changeIsEditing('description', true)}>
                    {profile?.description?.length ? PROFILE_SETTINGS.edit : PROFILE_SETTINGS.add}
                  </u>
                </div>
              )}
              {isProfileEdit && (
                <>
                  <h4 className="heading4 mt-4">{`Login ${
                    profile?.email ? `- ${profile?.email}` : ''
                  }`}</h4>
                  {isPasswordChange ? (
                    <div className="editInfo">
                      <form
                        className="media-body"
                        onSubmit={(e) => savePassword(e)}
                      >
                        <Input
                          autoComplete="off"
                          id="old_password"
                          label={PROFILE_SETTINGS.current_password}
                          name="old_password"
                          value={changePassword.old_password}
                          type="password"
                          onChange={(e) => handleChangePassword(e)}
                        />
                        <Input
                          autoComplete="off"
                          id="password"
                          label={PROFILE_SETTINGS.new_password}
                          name="password"
                          value={changePassword.password}
                          type="password"
                          error={isNotValidPassword}
                          helperText={PROFILE_SETTINGS.password_error}
                          onChange={(e) => handleChangePassword(e)}
                        />
                        <Input
                          autoComplete="off"
                          id="password_confirmation"
                          label={PROFILE_SETTINGS.confirm_password}
                          name="password_confirmation"
                          type="password"
                          value={changePassword.password_confirmation}
                          onChange={(e) => handleChangePassword(e)}
                          error={isNotMatch}
                          helperText={PROFILE_SETTINGS.confirm_password_error}
                        />
                        <Button
                          type={BUTTON_TYPES.main_save}
                          loading={isSavingProgress}
                          customClasses="btn cta-primary"
                          text={PROFILE_SETTINGS.save}
                        />
                      </form>
                      <u
                        onClick={() => {
                          setChangePassword({
                            old_password: '',
                            password: '',
                            password_confirmation: '',
                          });
                          setIsPasswordChange(false);
                        }}
                      >
                        {PROFILE_SETTINGS.cancel}
                      </u>
                    </div>
                  ) : (
                    <div className="editInfo">
                      <div className="media-body">
                        <h4>{PROFILE_SETTINGS.password}</h4>
                      </div>
                      <u onClick={() => setIsPasswordChange(true)}>{PROFILE_SETTINGS.update}</u>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
